import { useLazyQuery, useQuery } from '@apollo/client';
import { Box, Button, Skeleton, styled, Typography } from '@mui/material';
// import { useNavigate } from 'react-router';
import { useAppSelector } from 'src/app/hooks';
import {
  BillableMetric,
  PlanItem,
  PlanSimulationResponse,
  PlanSimulationV2Params,
  UserPackagePlan
} from 'src/models/register';
import {
  GET_BILLABLE_METRICS,
  GET_PLAN_SIMULATION_V2
} from 'src/services/graphql/register/query';
import { IPeriodOption } from '..';
import separateNumberFormat from 'src/utils/separateNumberFormat';
import { useEffect, useState } from 'react';
import { themeColors } from 'src/theme/schemes/PureLightTheme';
import useBillingSystem from 'src/services/useBillingSystem';
// import { getPromoCode } from 'src/utils/promoCodeUtils';
import { useLocation } from 'react-router';

interface Props {
  plan: UserPackagePlan;
  backgroundColor: string;
  period: IPeriodOption;
  hideCTAButton?: boolean;
  handleChoosePlan: (
    plan: UserPackagePlan,
    promoCodes: string[],
    planItems: PlanItem[],
    planSimulation: PlanSimulationResponse,
    referalCode?: string
  ) => void;
}

interface CustomBackground {
  customBackground: string;
}

const LiteTypography = styled(Typography)<CustomBackground>(
  ({ customBackground }) => `
  background: ${customBackground};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-transform: capitalize;
  `
);

const initCountryId = '9cd88e16-510e-49f0-9ce8-f4d27f0b85bb';

const contactLink =
  'https://api.whatsapp.com/send/?phone=628131134707&text=Halo%20NoLimit%2C%20saya%20ingin%20bertanya%20tentang%20produk%20NoLimit.%20Terima%20kasih.';

const PlanCard: React.FC<Props> = ({
  plan,
  backgroundColor,
  period,
  hideCTAButton = false,
  handleChoosePlan
}) => {
  const { pathname } = useLocation();
  const isRegister = pathname.includes('register');
  const countryId = useAppSelector(
    (state) => state.storeBilling.currentPlan.data.countryId
  );
  const { data: emailUserProfile } = useAppSelector(
    (state) => state.users.userProfile
  );
  const projectId = useAppSelector((state) => state?.users?.project?.id);

  const { useGetUserSubscription } = useBillingSystem();
  const { data: subscriptionData, loading: isLoadingSubsData } =
    useGetUserSubscription({
      variables: { projectId: projectId },
      skip: !projectId
    });

  // if user has no subscription, isFirstPayment is true
  const isFirstPayment = projectId
    ? subscriptionData?.billing_getUserSubscription?.isFirstPayment
    : true;

  const [planItems, setPlanItems] = useState<Array<PlanItem>>([]);

  const [getPlanSimulation, { data, loading, error }] = useLazyQuery<
    {
      billing_getPlanSimulationV2: PlanSimulationResponse;
    },
    PlanSimulationV2Params
  >(GET_PLAN_SIMULATION_V2, {
    fetchPolicy: 'cache-first' // Used for first execution
    // nextFetchPolicy: 'cache-first' // Used for subsequent executions
  });

  const {
    data: billableMetrics,
    loading: billableMetricsLoading,
    error: billableMetricsError
  } = useQuery<{ billing_getBillableMetrics: BillableMetric[] }>(
    GET_BILLABLE_METRICS,
    {
      fetchPolicy: 'cache-first',
      // nextFetchPolicy: 'cache-first',
      onCompleted: (data) => {
        // get billable metric ids for price calculation
        const metrics = data.billing_getBillableMetrics;
        const planItems = metrics
          .map((metric) => {
            if (metric.code === 'SOCIAL_PROFILE')
              return {
                billableMetricId: metric.id,
                quota: plan.basicObjectQuota
              };
            if (metric.code === 'KEYWORD_HASHTAG')
              return {
                billableMetricId: metric.id,
                quota: plan.advanceObjectQuota
              };
            if (metric.code === 'CLIPPING')
              return {
                billableMetricId: metric.id,
                quota: plan.onlineMediaObjectQuota
              };
            if (metric.code === 'ENTITY')
              return {
                billableMetricId: metric.id,
                quota: plan.entityQuota
              };
            return null;
          })
          .filter((item) => item !== null);

        setPlanItems(planItems as Array<PlanItem>);
        const isCustomPlan = plan.name === 'custom';

        getPlanSimulation({
          variables: {
            countryId: countryId || initCountryId,
            period: period.value,
            promoCodes: [],
            // : getPromoCode(plan.name, period.value, isFirstPayment),
            // promoCodes: [],
            planItems: isCustomPlan ? planItems : null,
            email: emailUserProfile?.email || '',
            referalCode: '',
            packagePlanId: !isCustomPlan ? plan.id : null
          }
        });
      }
    }
  );

  useEffect(() => {
    if (!billableMetrics) return;
    const planItems = billableMetrics.billing_getBillableMetrics
      .map((metric) => {
        if (metric.code === 'SOCIAL_PROFILE')
          return {
            billableMetricId: metric.id,
            quota: plan.basicObjectQuota
          };
        if (metric.code === 'KEYWORD_HASHTAG')
          return {
            billableMetricId: metric.id,
            quota: plan.advanceObjectQuota
          };
        if (metric.code === 'CLIPPING')
          return {
            billableMetricId: metric.id,
            quota: plan.onlineMediaObjectQuota
          };
        if (metric.code === 'ENTITY')
          return {
            billableMetricId: metric.id,
            quota: plan.entityQuota
          };
        return null;
      })
      .filter((item) => item !== null);

    setPlanItems(planItems as Array<PlanItem>);
    const isCustomPlan = plan.name === 'custom';

    getPlanSimulation({
      variables: {
        countryId: countryId || initCountryId,
        period: period.value,
        promoCodes: [],
        // : getPromoCode(plan.name, period.value, isFirstPayment),
        // promoCodes: [],
        planItems: isCustomPlan ? planItems : null,
        email: emailUserProfile?.email || '',
        referalCode: '',
        packagePlanId: !isCustomPlan ? plan.id : null
      }
    });
  }, [period, billableMetrics, plan, isRegister]);

  const simulation = data?.billing_getPlanSimulationV2;
  const isLoading = billableMetricsLoading || loading;

  const isCustomPlan = plan.displayName === 'custom';

  return (
    <>
      <LiteTypography
        variant="h2"
        fontWeight={800}
        customBackground={backgroundColor}
        gutterBottom
      >
        {plan.displayName}
      </LiteTypography>
      <Box mb={2.6}>
        {isLoading ? (
          <>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
          </>
        ) : simulation ? (
          <>
            {isCustomPlan ? (
              <Typography
                variant="subtitle1"
                component="h3"
                fontWeight={600}
                color={themeColors.darkTeal}
              >
                Set your own price
              </Typography>
            ) : (
              <Typography variant="body1" component="h3" fontWeight={800}>
                {`${simulation.currency.toUpperCase()} ${separateNumberFormat(
                  simulation.total
                )}`}
                <Typography
                  variant="subtitle2"
                  component="span"
                  fontWeight={600}
                >
                  /{period.period}
                </Typography>
              </Typography>
            )}

            {simulation.discount > 0 && (
              <Typography
                variant="body2"
                component="div"
                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
              >
                {`${simulation.currency.toUpperCase()} ${separateNumberFormat(
                  simulation.subTotal
                )}`}
              </Typography>
            )}

            {simulation.discount > 0 && (
              <Typography
                variant="caption"
                color={themeColors.blue}
                fontWeight={600}
              >
                {`(${separateNumberFormat(simulation.discount)} off)`}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body1" component="h3" fontWeight={800}>
            {`-`}
            <Typography variant="subtitle2" component="span" fontWeight={600}>
              /{period.period}
            </Typography>
          </Typography>
        )}
      </Box>
      {isLoading ? (
        <>
          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 3.8 }} />
        </>
      ) : (
        <>
          {(isCustomPlan || plan.basicObjectQuota > 0) && (
            <Typography variant="body2" component="h4">
              {isCustomPlan
                ? 'Any Social Profiles'
                : `${plan.basicObjectQuota} Social Profiles`}
            </Typography>
          )}
          {(isCustomPlan || plan.advanceObjectQuota > 0) && (
            <Typography variant="body2" component="h4">
              {isCustomPlan
                ? 'Any Hashtag / Keywords'
                : `${plan.advanceObjectQuota} Hashtag / Keywords`}
            </Typography>
          )}
          {(isCustomPlan || plan.entityQuota > 0) && (
            <Typography variant="body2" component="h4">
              {isCustomPlan ? 'Any Entities' : `${plan.entityQuota} Entities`}
            </Typography>
          )}
          {(isCustomPlan || plan.onlineMediaObjectQuota > 0) && (
            <Typography variant="body2" component="h4" mb={3.8}>
              {isCustomPlan
                ? 'Any Clippings'
                : `${plan.onlineMediaObjectQuota} Clippings`}
            </Typography>
          )}
        </>
      )}
      {!hideCTAButton ? (
        <Box textAlign="center">
          <Button
            variant="contained"
            color="primary"
            sx={{ width: '100%', fontWeight: '600' }}
            disabled={isLoading}
            onClick={() => {
              if (isCustomPlan || !isRegister) {
                window.open(contactLink, '_blank');
              } else {
                handleChoosePlan(
                  plan,
                  [],
                  // : getPromoCode(plan.name, period.value, isFirstPayment),
                  // [],
                  planItems,
                  simulation,
                  data?.billing_getPlanSimulationV2?.referralCode
                );
              }
            }}
          >
            {isCustomPlan || !isRegister ? 'Contact Us' : 'Choose plan'}
            {/* Contact Us */}
          </Button>
        </Box>
      ) : null}
    </>
  );
};

export default PlanCard;
